import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OnboardingState } from '../config/state.model';
import { AuthService } from './auth.service';
import { buildRowPayload } from './payload-builder';

@Injectable({ providedIn: 'root' })
export class RiskApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private headers(token: string): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `JWT ${token}` });
  }

  async submit(state: OnboardingState, baseUrl: string): Promise<void> {
    const { token } = await this.auth.ensureAuth();
    const payload = state.rows
      .map(row => buildRowPayload(state, row))
      .filter(r => !!r['risk_domain']);
    await firstValueFrom(
      this.http.post<any>(`${baseUrl}/risk/risks/bulk-import/`, payload, { headers: this.headers(token) })
    );
  }

  async fetchProjects(baseUrl: string): Promise<{ id: number; project_name: string }[]> {
    const { token } = await this.auth.ensureAuth();
    return firstValueFrom(
      this.http.get<{ id: number; project_name: string }[]>(
        `${baseUrl}/risk/project-risks/projects/`,
        { headers: this.headers(token) }
      )
    );
  }

  /**
   * Part 2 of the risk onboarding flow. Uploads a mitigation/register Excel and
   * maps it onto the risks already imported by `submit()` — creating ProjectRisks,
   * mitigation tasks, owners and project-level controls.
   *
   * Multipart upload: do NOT set Content-Type (the browser adds the boundary).
   */
  async uploadMitigations(file: File, projectName: string, baseUrl: string): Promise<any> {
    const { token } = await this.auth.ensureAuth();
    const form = new FormData();
    form.append('file', file);
    form.append('project_name', projectName);
    const headers = new HttpHeaders({ Authorization: `JWT ${token}` });
    return firstValueFrom(
      this.http.post<any>(
        `${baseUrl}/risk/project-risks/upload-mitigation-excel/`,
        form,
        { headers }
      )
    );
  }
}
